import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs, type BankSummary, type BillingItem, type Contract, type Installment, type Paged, type ProjectDetail } from '../api';
import { Empty, ErrorAlert, Field, Loading } from '../components/ui';
import { date, hm, MODALITY_LABEL, money, parseMoneyInput, todayIso } from '../format';
import { BalanceBadge } from './BancosHoras';

const BILLING_LABEL = { nao_faturado: 'não faturado', faturado: 'faturado', cancelado: 'cancelado', ajustado: 'ajustado' } as const;

export function ContractDetailPage() {
  const { id } = useParams();
  const c = useQuery({ queryKey: ['contract', id], queryFn: () => api.get<Contract>(`/contracts/${id}`) });
  const project = useQuery({ queryKey: ['project', c.data?.projectId], queryFn: () => api.get<ProjectDetail>(`/projects/${c.data!.projectId}`), enabled: Boolean(c.data) });
  if (c.isLoading || project.isLoading) return <Loading />;
  if (c.error) return <ErrorAlert error={c.error} />;
  const k = c.data!;
  const p = project.data!;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to={`/projetos/${p.id}`}>← {p.name}</Link>
          </p>
          <h1>
            Contrato {k.code} <span className="badge">{MODALITY_LABEL[k.modality]}</span>{' '}
            <span className={`badge ${k.status === 'ativo' ? 'badge-ok' : 'badge-off'}`}>{k.status}</span>
          </h1>
          <p>
            {p.clientTradeName || p.clientName} · vigência {date(k.startDate)} a {date(k.endDate)}
          </p>
        </div>
        <div className="actions">
          <Link className="btn" to={`/contratos/${k.id}/editar`}>
            Editar
          </Link>
        </div>
      </div>
      <section className="card">
        <dl className="props">
          {k.hourlyRate && (
            <>
              <dt>Valor da hora</dt>
              <dd>{money(k.hourlyRate)}</dd>
            </>
          )}
          {k.contractedMinutes != null && (
            <>
              <dt>Horas</dt>
              <dd>{hm(k.contractedMinutes)}</dd>
            </>
          )}
          {k.totalValue && (
            <>
              <dt>Valor total</dt>
              <dd>{money(k.totalValue)}</dd>
            </>
          )}
          {k.monthlyValue && (
            <>
              <dt>Mensalidade</dt>
              <dd>{money(k.monthlyValue)}</dd>
            </>
          )}
          <dt>Referência externa</dt>
          <dd>{k.externalReference || '—'}</dd>
        </dl>
      </section>
      {k.modality === 'banco_horas' && <BankPanel contract={k} />}
      {k.modality === 'mensalidade' && <MonthlyPanel contract={k} />}
      {k.modality === 'valor_fechado' && <InstallmentsPanel contract={k} />}
      {k.modality === 'avulso' && (
        <section className="card">
          <h2>Cobrança</h2>
          <p className="small muted">Cada execução confirmada gera um item faturável (horas × {money(k.hourlyRate)}).</p>
        </section>
      )}
      <ContractItems contractId={k.id} />
    </>
  );
}

function BankPanel({ contract }: { contract: Contract }) {
  const q = useQuery({ queryKey: ['balance', contract.id], queryFn: () => api.get<BankSummary & { contractedMinutes: number }>(`/contracts/${contract.id}/balance`) });
  return (
    <section className="card">
      <div className="card-head">
        <h2>Banco de horas</h2>
        <Link className="btn btn-sm btn-primary" to={`/bancos-de-horas/${contract.id}`}>
          Abrir extrato
        </Link>
      </div>
      {q.data &&
        (q.data.hasInitialCredit ? (
          <p>
            <BalanceBadge minutes={q.data.balance} low={contract.lowBalanceMinutes != null && q.data.balance <= contract.lowBalanceMinutes} /> · consumido {hm(q.data.consumed - q.data.reversed)} de{' '}
            {hm(q.data.credits)}
            {q.data.pendingOverageCount > 0 && <> · <span className="badge badge-demo">excedente pendente {hm(q.data.pendingOverageMinutes)}</span></>}
          </p>
        ) : (
          <div className="alert alert-warning">Crédito inicial não registrado. Abra o extrato para registrar a contratação ou o saldo de abertura.</div>
        ))}
    </section>
  );
}

function MonthlyPanel({ contract }: { contract: Contract }) {
  const qc = useQueryClient();
  const [competence, setCompetence] = useState(todayIso().slice(0, 7));
  const m = useMutation({
    mutationFn: () => api.post<{ id: string; created: boolean }>(`/contracts/${contract.id}/monthly-items`, { competence }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contract-items', contract.id] }),
  });
  return (
    <section className="card">
      <h2>Mensalidade</h2>
      <p className="small muted">Atendimentos não geram cobrança por sessão. Gere um item por competência; repetir não duplica.</p>
      <ErrorAlert error={m.error} />
      {m.data && <div className="alert alert-success">{m.data.created ? 'Item da competência gerado.' : 'O item desta competência já existia; nada foi duplicado.'}</div>}
      <div className="toolbar">
        <label className="visually-hidden" htmlFor="m-comp">Competência</label>
        <input id="m-comp" type="month" value={competence} onChange={(e) => setCompetence(e.target.value)} style={{ flex: '0 1 200px' }} />
        <button className="btn btn-primary" disabled={!competence || m.isPending} onClick={() => m.mutate()}>
          Gerar item de {money(contract.monthlyValue)}
        </button>
      </div>
    </section>
  );
}

function InstallmentsPanel({ contract }: { contract: Contract }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['installments', contract.id], queryFn: () => api.get<{ data: Installment[] }>(`/contracts/${contract.id}/installments`) });
  const [form, setForm] = useState({ description: '', dueDate: todayIso(), amount: '' });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['installments', contract.id] });
    void qc.invalidateQueries({ queryKey: ['contract-items', contract.id] });
  };
  const add = useMutation({
    mutationFn: () => {
      const amount = parseMoneyInput(form.amount);
      if (!amount) throw new Error('Valor inválido. Use, por exemplo, 6.000,00.');
      return api.post(`/contracts/${contract.id}/installments`, { ...form, amount });
    },
    onSuccess: () => {
      setForm({ description: '', dueDate: todayIso(), amount: '' });
      refresh();
    },
  });
  const bill = useMutation({ mutationFn: (id: string) => api.post(`/installments/${id}/bill`), onSuccess: refresh });
  const del = useMutation({ mutationFn: (id: string) => api.del(`/installments/${id}`), onSuccess: refresh });
  const sum = q.data?.data.reduce((s, i) => s + Math.round(Number(i.amount) * 100), 0) ?? 0;
  return (
    <section className="card">
      <h2>Parcelas / marcos</h2>
      <p className="small muted">
        Programado {money((sum / 100).toFixed(2))} de {money(contract.totalValue)}. Execuções registram esforço e não geram cobrança por hora; cobrar acima do preço exige
        aditivo.
      </p>
      <ErrorAlert error={add.error ?? bill.error ?? del.error} />
      {q.data?.data.length === 0 ? (
        <Empty>Nenhuma parcela.</Empty>
      ) : (
        <div className="list">
          {q.data?.data.map((i) => (
            <div key={i.id} className="list-item" style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span>
                <strong>{i.description}</strong> · {date(i.dueDate)} · {money(i.amount)}{' '}
                {i.billingStatus && <span className="badge badge-brand">item {BILLING_LABEL[i.billingStatus]}</span>}
              </span>
              <span className="actions">
                {!i.billingItemId && (
                  <>
                    <button className="btn btn-sm" disabled={bill.isPending} onClick={() => bill.mutate(i.id)}>
                      Gerar item faturável
                    </button>
                    <button className="btn btn-sm btn-ghost" onClick={() => window.confirm('Excluir esta parcela?') && del.mutate(i.id)}>
                      Excluir
                    </button>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="form-row" style={{ marginTop: '1rem', alignItems: 'end' }}>
        <Field label="Descrição">{(p) => <input {...p} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Parcela 1/3" />}</Field>
        <div className="form-row">
          <Field label="Vencimento">{(p) => <input {...p} type="date" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />}</Field>
          <Field label="Valor (R$)">{(p) => <input {...p} inputMode="decimal" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />}</Field>
        </div>
      </div>
      <button className="btn btn-primary" style={{ marginTop: '0.5rem' }} disabled={form.description.trim().length < 2 || !form.amount || add.isPending} onClick={() => add.mutate()}>
        Adicionar parcela
      </button>
    </section>
  );
}

function ContractItems({ contractId }: { contractId: string }) {
  const q = useQuery({ queryKey: ['contract-items', contractId], queryFn: () => api.get<Paged<BillingItem>>(`/billing-items${qs({ queue: 'all', contractId, pageSize: 50 })}`) });
  return (
    <section className="card">
      <div className="card-head">
        <h2>Itens faturáveis do contrato</h2>
        <Link to="/faturamento" className="small">
          Ir para o faturamento
        </Link>
      </div>
      {q.data?.data.length === 0 ? (
        <Empty>Nenhum item faturável.</Empty>
      ) : (
        <table className="table">
          <tbody>
            {q.data?.data.map((i) => (
              <tr key={i.id}>
                <td className="small">{i.description}</td>
                <td className="small">{BILLING_LABEL[i.billingStatus]}</td>
                <td style={{ textAlign: 'right' }}>{money(i.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
